<template>
    <div :class="[{ dark_theme: theme == THEMES.DARK, dark: theme == THEMES.DARK }]">
        <div v-if="!authInitialized" class="d-flex justify-content-center align-items-center py-5">
            <img src="@/assets/Triangles-1s-200px.gif" alt="" />
        </div>
        <div v-else>
            <div v-if="!authenticated" class="login-container">
                <LoginForm />
            </div>
            <div v-else :class="['app-layout', { 'sidebar-collapsed': !showSidebar }]">
                <!-- Sidebar -->
                <div class="sidebar-wrapper">
                    <Sidebar />
                </div>
                <button
                    v-if="showSidebar"
                    type="button"
                    class="sidebar-backdrop"
                    aria-label="Close navigation"
                    @click="setSidebarVisibility(false)"
                ></button>
                
                <!-- Main Content Area -->
                <div class="main-content-wrapper">
                    <!-- Header -->
                    <Header />
                    
                    <!-- Page Content -->
                    <div class="page-content">
                        <div class="container-fluid page-container">
                            <router-view />
                        </div>

                        <!-- Footer -->
                        <div class="footer-container">
                            <div class="container">
                                <div class="row">
                                    <div class="col-12 p-4 ps-0 text-left">&copy; Copyright {{ appName }}, All rights reserved.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex"
import Sidebar from "./components/layout/Sidebar.vue"
import Header from "./components/layout/Header.vue"
import LoginForm from "./components/layout/LoginForm.vue"
import { ROUTE_PATHS } from "./router/route-paths"

export default {
    name: "App",
    computed: {
        ...mapGetters({
            authenticated: "auth/authenticated",
        }),
        ...mapState("auth", ["authInitialized"]),
        ...mapState("auth", ["user"]),
        ...mapState("misc", ["showSidebar", "theme", "THEMES"]),
    },
    components: { Sidebar, LoginForm, Header },
    data() {
        return { loggingOut: false, appName: process.env.VUE_APP_NAME }
    },
    methods: {
        ...mapActions("auth", {
            initAuth: "initAuth",
            logoutAuth: "logout",
        }),
        ...mapActions("misc", ["initTheme", "setData"]),
        async handleAuthExpired() {
            if (this.loggingOut) return
            this.loggingOut = true
            try {
                await this.logoutAuth()
                if (this.$route.path !== ROUTE_PATHS.LOGIN) {
                    this.$router.replace(ROUTE_PATHS.LOGIN).catch(() => {})
                }
            } finally {
                this.loggingOut = false
            }
        },
        setSidebarVisibility(showSidebar) {
            this.setData({ showSidebar })
        },
        syncSidebarForViewport() {
            if (window.innerWidth <= 768 && this.showSidebar) {
                this.setSidebarVisibility(false)
            }
        },
    },
    async mounted() {
        window.addEventListener("auth:expired", this.handleAuthExpired)
        window.addEventListener("resize", this.syncSidebarForViewport)
        await this.initTheme()
        await this.initAuth()
        this.syncSidebarForViewport()
    },
    beforeDestroy() {
        window.removeEventListener("auth:expired", this.handleAuthExpired)
        window.removeEventListener("resize", this.syncSidebarForViewport)
    },
}
</script>

<style lang="scss">
@import "@/assets/variables.scss";

#app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: $textColor;
    /* Remove artificial top offset so header sits flush with top */
    margin-top: 0;
    min-height: 100%;
}
html,
body {
    height: 100%;
}
body {
    font-size: 0.8em;
}
* {
    transition: background-color 0.2s;
}
.main-bg {
    background-color: $backgroundColor;
}
.theme-body {
    background: $backgroundColor;
    &.dark {
        background-color: $backgroundColorDark;
        color: $darkTextColor;
    }
}

/* Login Page Styling */
.login-container {
    padding: 2rem;
    min-height: 100vh;
    background-color: $backgroundColor;
}

/* Dark theme for login container */
.dark_theme .login-container {
    background-color: $backgroundColorDark;
    color: $darkTextColor;
}

/* Main App Layout */
.app-layout {
    display: grid;
    grid-template-columns: $sidebarWidth 1fr;
    height: 100vh;
    min-height: 100vh;
    max-height: 100vh;
    overflow: hidden;
    transition: grid-template-columns 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &.sidebar-collapsed {
        grid-template-columns: 0px 1fr;
    }
}

/* Sidebar Wrapper */
.sidebar-wrapper {
    /* Sidebar stays in the grid column; only its internal content scrolls. */
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    top: 0;
    align-self: stretch;
    height: 100vh;
    min-height: 100vh;
    max-height: 100vh;
    z-index: 200;
    background: linear-gradient(180deg, $primaryColor 0%, darken($primaryColor, 5%) 100%);
    
    /* Add connection shadow to header */
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: -12px;
        width: 12px;
        height: 100%;
        background: linear-gradient(90deg, 
            rgba(13, 45, 44, 0.15) 0%, 
            rgba(13, 45, 44, 0.05) 50%,
            transparent 100%
        );
        pointer-events: none;
        z-index: 10;
    }
    
    .sidebar-collapsed & {
        transform: translateX(-100%);
    }
    
    /* Dark theme shadow adjustment */
    .dark_theme & {
        background: linear-gradient(180deg, $primaryColorDark 0%, darken($primaryColorDark, 8%) 100%);
    }

    .dark_theme &::after {
        background: linear-gradient(90deg, 
            rgba(186, 142, 88, 0.2) 0%, 
            rgba(186, 142, 88, 0.08) 50%,
            transparent 100%
        );
    }
}

.sidebar-backdrop {
    display: none;
    border: 0;
    padding: 0;
}

/* Main Content Wrapper */
.main-content-wrapper {
    display: flex;
    flex-direction: column;
    height: 100vh;
    min-height: 0;
    max-height: 100vh;
    background: linear-gradient(135deg, $backgroundColor 0%, lighten($backgroundColor, 2%) 100%);
    position: relative;
    z-index: 300;
    overflow: visible;
    min-width: 0;
    
    .dark_theme & {
        /* Remove any gradient/blur that could look like haze */
        background: none !important;
        background-color: $backgroundColorDark !important;
        color: $darkTextColor;
    }
}

/* Page Content */
.page-content {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
    padding: 12px 0; /* Add some vertical breathing room */
    overflow-x: hidden;
    overflow-y: auto;
}

.page-container {
    /* Unified content container width and horizontal gutters */
    padding-top: 0px; /* Reduced padding for better visual spacing */
    max-width: $containerMaxWidth;
    margin-left: auto;
    margin-right: auto;
    padding-left: $containerGutter;
    padding-right: $containerGutter;
}

/* Ensure bootstrap containers don’t introduce translucent backgrounds in dark */
.dark_theme .container,
.dark_theme .container-fluid {
    background: transparent !important;
}

/* Footer */
.footer-container {
    margin-top: 24px;
    border-top: 1px solid $lightBorderColor;
    background-color: rgba(248, 247, 250, 0.8);
    backdrop-filter: blur(8px);
    
    .dark_theme & {
        border-color: $darkBorderColor;
        background: linear-gradient(135deg, 
            rgba(21, 38, 35, 0.9) 0%, 
            rgba(10, 27, 26, 0.8) 100%
        );
        color: rgba(232, 240, 239, 0.8);
        backdrop-filter: none;
    }
}

@media (max-width: 768px) {
    .app-layout,
    .app-layout.sidebar-collapsed {
        grid-template-columns: minmax(0, 1fr);
    }

    .sidebar-wrapper {
        position: fixed;
        left: 0;
        top: 0;
        bottom: 0;
        width: min(#{$sidebarWidth}, calc(100vw - 56px));
        height: 100vh;
        min-height: 100vh;
        max-height: 100vh;
        z-index: 1100;
        transform: translateX(0);
        box-shadow: 8px 0 24px rgba(0, 0, 0, 0.28);

        &::after {
            display: none;
        }

        .sidebar-collapsed & {
            pointer-events: none;
            transform: translateX(-100%);
        }
    }

    .sidebar-backdrop {
        display: block;
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: min(#{$sidebarWidth}, calc(100vw - 56px));
        background: rgba(13, 45, 44, 0.45);
        z-index: 1050;
        cursor: pointer;
    }

    .main-content-wrapper {
        min-width: 0;
        width: 100vw;
    }

    .page-content {
        padding: 8px 0;
        overflow-x: auto;
    }

    .page-container {
        max-width: 100%;
        padding-left: 12px;
        padding-right: 12px;
    }

    .footer-container {
        margin-top: 12px;

        .container {
            max-width: 100%;
        }
    }
}
</style>
